import { CASTLE_FIXED_TYPES } from '@/town/layout';
// Замок изнутри по расе: у эльфов, гномов и орков свои здания и фон, у людей — общая графика.
// Ресурсные здания (поля) у всех рас общие.
import fit from '@/town/raceBuildingFit.json';
import type { RaceKey } from '@sk/shared';
import { buildingArtUrl } from './artAssets';
import { CASTLE_BG } from './backgrounds';

/** Версия рисунков рас — менять при их замене (scripts/fit-race-buildings.py). */
const RACE_ART_V = 'r1';

const RACES_WITH_ART = new Set<string>(['elves', 'dwarves', 'orcs']);
const RACE_ART_TYPES = new Set<string>([...CASTLE_FIXED_TYPES, 'town_hall', 'wall']);

type Race = RaceKey | string | null | undefined;

const hasArt = (race: Race): race is string => !!race && RACES_WITH_ART.has(race);

export function raceBuildingArtUrl(type: string, race: Race): string | null {
  if (hasArt(race) && RACE_ART_TYPES.has(type)) return `/art/buildings/${race}/${type}.webp?${RACE_ART_V}`;
  return buildingArtUrl(type);
}

export function raceCastleBg(race: Race): string {
  return hasArt(race) ? `/art/castle/races/${race}.webp?${RACE_ART_V}` : CASTLE_BG;
}

/** Всё, что нужно для первого показа замка этой расы. */
export function raceCastleImages(race: Race): string[] {
  return hasArt(race)
    ? [raceCastleBg(race), ...[...RACE_ART_TYPES].map((type) => raceBuildingArtUrl(type, race)!)]
    : [];
}

/**
 * Прямоугольник рисунка внутри спрайта: подгонка под площадку сжимает рисунок к якорю и сдвигает
 * его (доли ширины спрайта). По этому прямоугольнику ставятся человечки — они идут за рисунком.
 */
export function raceArtRect<R extends { x: number; y: number; w: number; h: number }>(
  r: R,
  anchor: { x: number; y: number },
  type: string,
  race: Race,
): R {
  const entry = hasArt(race) ? (fit as Record<string, Record<string, number[]>>)[race]?.[type] : undefined;
  if (!entry) return r;
  const [scale = 1, dx = 0, dy = 0] = entry;
  return {
    ...r,
    x: anchor.x + (r.x - anchor.x) * scale + r.w * dx,
    y: anchor.y + (r.y - anchor.y) * scale + r.w * dy,
    w: r.w * scale,
    h: r.h * scale,
  };
}
